<template>
  <div class="deco-canvas-wrap" :class="{ 'is-circular': canvasStore.isCircularLabel }">
    <v-stage
      ref="stageRef"
      :config="stageConfig"
      @mousedown="onStageClick"
      @touchstart="onStageClick"
    >
      <!-- 白背景 -->
      <v-layer>
        <v-rect :config="bgConfig" />
      </v-layer>

      <!-- コンテンツ (写真・フレーム・スタンプ・テキスト) + Transformer -->
      <v-layer ref="contentLayerRef">
        <template v-for="obj in canvasStore.objects" :key="obj.id">
          <DrawableImage v-if="obj.objectType !== 'text'" :obj="obj" />
          <DrawableText v-else :obj="obj" />
        </template>
        <!-- Konva 組み込みTransformer (リサイズ・回転ハンドル) -->
        <v-transformer ref="transformerRef" :config="transformerConfig" />
      </v-layer>

      <!-- オーバーレイ (円形ラベル時: 境界ガイドリング) -->
      <v-layer :config="{ listening: false }">
        <v-circle v-if="canvasStore.isCircularLabel" :config="circleGuideConfig" />
      </v-layer>
    </v-stage>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, defineAsyncComponent } from 'vue'
import { useCanvasStore } from '../../stores/useCanvasStore'

const DrawableImage = defineAsyncComponent(() => import('./DrawableImage.vue'))
const DrawableText = defineAsyncComponent(() => import('./DrawableText.vue'))

const canvasStore = useCanvasStore()
const stageRef = ref(null)
const contentLayerRef = ref(null)
const transformerRef = ref(null)

const stageConfig = computed(() => ({
  width:  canvasStore.labelRegion.width  * canvasStore.displayScale,
  height: canvasStore.labelRegion.height * canvasStore.displayScale,
  scaleX: canvasStore.displayScale,
  scaleY: canvasStore.displayScale,
}))

const bgConfig = computed(() => ({
  x: 0,
  y: 0,
  width:  canvasStore.labelRegion.width,
  height: canvasStore.labelRegion.height,
  fill: 'white',
}))

// Transformer スタイル設定
const transformerConfig = computed(() => {
  const selectedObj = canvasStore.selectedObject
  const isText = selectedObj?.objectType === 'text'
  return {
    anchorSize: 20,
    anchorCornerRadius: 10,
    anchorFill: '#fff',
    anchorStroke: '#1a73e8',
    anchorStrokeWidth: 2,
    anchorHitStroke: 20,
    borderStroke: '#1a73e8',
    borderDash: [5, 5],
    rotateEnabled: true,
    keepRatio: true,
    enabledAnchors: ['top-left', 'top-right', 'bottom-left', 'bottom-right'],
    boundBoxFunc(oldBox, newBox) {
      if (Math.abs(newBox.width) < 20 || Math.abs(newBox.height) < 20) return oldBox
      return newBox
    },
  }
})

// 選択オブジェクトのサイズ/フォントが変わったらTransformerを強制更新
watch(
  () => {
    const o = canvasStore.selectedObject
    return o ? [o.width, o.height, o.fontSize] : null
  },
  () => {
    nextTick(() => {
      const transformer = transformerRef.value?.getNode()
      if (transformer && canvasStore.selectedId) {
        transformer.forceUpdate()
        transformer.getLayer()?.batchDraw()
      }
    })
  }
)

// 選択IDが変わったらTransformerをそのノードにアタッチ
watch(() => canvasStore.selectedId, (id) => {
  nextTick(() => {
    const transformer = transformerRef.value?.getNode()
    if (!transformer) return

    if (!id) {
      transformer.nodes([])
      transformer.getLayer()?.batchDraw()
      return
    }

    const selectedObj = canvasStore.objects.find(o => o.id === id)
    // フレーム: Transformer 不使用
    // テキストで lockResize + lockPosition が両方 true: 操作する余地がないので Transformer 非表示
    // 画像で movable=false + scalable=false: 同様に Transformer 非表示
    const isFullyLockedText = selectedObj?.objectType === 'text'
      && selectedObj.lockResize === true
      && selectedObj.lockPosition === true
    const isFullyLockedImage = selectedObj
      && selectedObj.objectType !== 'text'
      && selectedObj.objectType !== 'frame'
      && selectedObj.movable === false
      && selectedObj.scalable === false
    if (selectedObj?.objectType === 'frame' || isFullyLockedText || isFullyLockedImage) {
      transformer.nodes([])
      transformer.getLayer()?.batchDraw()
    } else {
      const stage = stageRef.value?.getNode()
      const node = stage?.findOne('#node-' + id)
      if (node) {
        transformer.nodes([node])
        transformer.getLayer()?.batchDraw()
      }
    }
  })
})

// 円形ラベル時のみ: 境界を示すガイドリング (CSSクリップと組み合わせて使用)
const circleGuideConfig = computed(() => {
  const { width, height } = canvasStore.labelRegion
  return {
    x: width / 2,
    y: height / 2,
    radius: Math.min(width, height) / 2 - 2,
    stroke: '#aaaaaa',
    strokeWidth: 3,
    dash: [12, 12],
    listening: false,
  }
})

function onStageClick(e) {
  if (e.target === stageRef.value?.getNode()) {
    canvasStore.deselectAll()
  }
}

defineExpose({ stageRef })
</script>
